import { Map as MlMap } from "maplibre-gl";
import { environment } from "../../../environments/environment";
import { DemPack, getDemPacks } from "../terrain/dem-packs";
import { ensureDemSources, ensureTerrainSource } from "./webgl-maplibre.dem-source";

export const TERRAIN_EXAGGERATION = 1;
const DEFAULT_PITCH_ON = 55;
const MAX_PITCH_ON = 85;
const MAX_PITCH_OFF = 60;
const EASE_MS = 500;

export async function has3dTerrainPacks(): Promise<boolean> {
  if (!environment.enableThreeD) return false;
  const packs = await getDemPacks();
  return packs.length > 0;
}

// Module-level state for the single terrain binding. MapLibre supports one
// terrain source globally; we rebind when the viewport center crosses pack
// bbox boundaries so a regional + Europe-wide install renders the higher-
// priority covering pack in its bbox and falls back to the broader one
// elsewhere — instead of going flat outside `packs[0]`.
let currentTerrainSourceId: string | null = null;
let moveEndHandler: (() => void) | null = null;
let boundMap: MlMap | null = null;

// Wipe module-level state so a subsequent map instance rebinds terrain from
// scratch. Without this, `applyTerrain` short-circuits on the deterministic
// per-pack source id and the new map renders flat.
export function resetTerrain3dState(): void {
  if (boundMap && moveEndHandler) boundMap.off("moveend", moveEndHandler);
  currentTerrainSourceId = null;
  moveEndHandler = null;
  boundMap = null;
}

function pickCoveringPack(packs: DemPack[], lng: number, lat: number): DemPack | null {
  // For terrain we want the WIDEST zoom coverage at the camera location, not
  // the highest-priority (regional) pack — a high-res pack like cop30-at only
  // has tiles at z≥11, leaving the view flat below that. cop90-europe covers
  // z≥4 natively, so terrain is visible at typical flight-app zooms.
  const covering = packs.filter((p) => {
    const [w, s, e, n] = p.manifest.bbox;
    return lng >= w && lng <= e && lat >= s && lat <= n;
  });
  const byMinZoom = covering.sort((a, b) => a.manifest.min_zoom - b.manifest.min_zoom);
  if (byMinZoom.length > 0) return byMinZoom[0];
  // No covering pack — fall back to the broadest installed pack regardless of
  // bbox, so the user still sees terrain near the edge of a regional install.
  const broadest = [...packs].sort((a, b) => a.manifest.min_zoom - b.manifest.min_zoom);
  return broadest[0] ?? null;
}

function applyTerrain(map: MlMap, packs: DemPack[]): void {
  // A WebGL context restore replaces the style and fires moveend before it has
  // loaded, and addSource then throws. The service rebuilds 3D on style.load.
  if (!map.style?._loaded) return;
  const c = map.getCenter();
  const pack = pickCoveringPack(packs, c.lng, c.lat);
  if (!pack) return;
  // Terrain binds to a dedicated source variant so it doesn't alias the
  // hillshade source on the same pack — MapLibre warns and degrades
  // rendering quality when one source backs both features.
  const sourceId = ensureTerrainSource(map, pack);
  if (sourceId === currentTerrainSourceId) return;
  currentTerrainSourceId = sourceId;
  console.log(
    `[terrain3d] bind pack=${pack.manifest.id} source=${sourceId} center=${c.lng.toFixed(3)},${c.lat.toFixed(3)}`
  );
  map.setTerrain({ source: sourceId, exaggeration: TERRAIN_EXAGGERATION });
}

/**
 * MapLibre supports a single terrain source globally. We install raster-dem
 * sources for every pack and rebind on `moveend` to the covering pack at
 * map center — so a regional + broader DEM combination renders correctly
 * in both areas. If no pack covers, the highest-priority pack stays bound.
 *
 * Mirrors maplibre's 3d-terrain example: raised maxPitch + steep default
 * pitch + a sky layer so the horizon doesn't read as a flat tilted plane.
 */
export async function show3dTerrain(map: MlMap): Promise<void> {
  if (!environment.enableThreeD) return;
  const packs = await ensureDemSources(map);
  if (packs.length === 0) {
    console.warn("[terrain3d] no DEM packs installed");
    return;
  }
  map.setMaxPitch(MAX_PITCH_ON);
  map.setPitch(DEFAULT_PITCH_ON);
  map.setSky({
    "sky-color": "#5aa1d8",
    "sky-horizon-blend": 0.6,
    "horizon-color": "#cfe2f2",
    "horizon-fog-blend": 0.5,
    "fog-color": "#ffffff",
    "fog-ground-blend": 0.5,
  });
  applyTerrain(map, packs);

  // Re-bind on viewport change. Guard against repeat enables on the same
  // map (clear prior handler) and a stale map reference (different MlMap).
  if (boundMap && moveEndHandler) boundMap.off("moveend", moveEndHandler);
  moveEndHandler = () => applyTerrain(map, packs);
  map.on("moveend", moveEndHandler);
  boundMap = map;
}

export function hide3dTerrain(map: MlMap): void {
  if (boundMap && moveEndHandler) {
    boundMap.off("moveend", moveEndHandler);
  }
  moveEndHandler = null;
  boundMap = null;
  currentTerrainSourceId = null;

  map.setTerrain(null);
  // `setSky(undefined)` is a no-change in MapLibre 5, not a clear. Pass
  // fully-transparent paints so the horizon gradient and fog disappear in
  // 2D — equivalent to "no sky".
  map.setSky({
    "sky-color": "rgba(0,0,0,0)",
    "horizon-color": "rgba(0,0,0,0)",
    "fog-color": "rgba(0,0,0,0)",
    "sky-horizon-blend": 0,
    "horizon-fog-blend": 0,
    "fog-ground-blend": 0,
  });
  if (map.getPitch() !== 0) {
    map.easeTo({ pitch: 0, duration: EASE_MS });
  }
  map.setMaxPitch(environment.enableThreeD ? MAX_PITCH_OFF : 0);
}
